import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronDown } from "lucide-react";
import { useMakes } from "@/hooks/useMakes";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { useLanguage } from "@/context/LanguageContext";
import { vehicleCardUILabels, commonButtonLabels } from "@/i18n";
import { valuationLabels } from "@/i18n";

interface EngineSelectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedMake: string;
  selectedEngine: string;
  onSelect: (engine: string) => void;
  vehicleType?: "car" | "mc";
}

export const EngineSelectionDialog = ({
  open,
  onOpenChange,
  selectedMake,
  selectedEngine,
  onSelect,
  vehicleType = "car",
}: EngineSelectionDialogProps) => {
  const { language } = useLanguage();
  const { isNative } = useNativePlatform();
  const vcLabels = vehicleCardUILabels(language);
  const valLabels = valuationLabels(language);
  const btnLabels = commonButtonLabels(language);

  const [showAllEngines, setShowAllEngines] = useState(false);
  const [showEngineOtherInput, setShowEngineOtherInput] = useState(false);
  const [engineOtherText, setEngineOtherText] = useState("");

  const { makes, isLoadingMakes } = useMakes(true);

  // Filter makes based on vehicle type
  const filteredMakes = useMemo(() => {
    if (!makes.length) return [];
    return makes.filter((m) => (vehicleType === "mc" ? m.isMC : !m.isMC));
  }, [makes, vehicleType]);

  // Get selected make object
  const selectedMakeObj = useMemo(() => {
    if (!selectedMake || !filteredMakes.length) return null;
    return filteredMakes.find((m) => m.name === selectedMake);
  }, [filteredMakes, selectedMake]);

  // Get engines for selected make (excluding "Övrigt"/"Other")
  const availableEngines = useMemo(() => {
    if (!selectedMakeObj) return [];
    const otherLabel = language === "en" ? "Other" : "Övrigt";
    return selectedMakeObj.engines.filter((e) => e.name !== otherLabel);
  }, [selectedMakeObj, language]);

  // Display engines: for 4 columns, show 7 engines + Övrigt = 8 items (2 rows)
  const displayedEngines = useMemo(() => {
    if (availableEngines.length > 0) {
      if (showAllEngines) {
        return availableEngines;
      }
      // Show 7 engines (to fill 2 rows of 4 columns, with Övrigt as 8th item)
      return availableEngines.slice(0, 7);
    }
    // Fallback to empty if no engines available
    return [];
  }, [availableEngines, showAllEngines]);

  const handleEngineSelect = (engineName: string) => {
    onSelect(engineName);
    onOpenChange(false);
    setShowEngineOtherInput(false);
    setEngineOtherText("");
  };

  const handleEngineOtherClick = () => {
    setShowEngineOtherInput(true);
    // If we already have an engine value, pre-fill it
    if (selectedEngine && !engineOtherText) {
      setEngineOtherText(selectedEngine);
    }
  };

  const handleEngineOtherSubmit = () => {
    if (engineOtherText.trim()) {
      handleEngineSelect(engineOtherText.trim());
    }
  };

  if (!selectedMake) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent nested={isNative}>
          <DialogHeader>
            <DialogTitle>{vcLabels.selectEngineTitle}</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground text-center py-8">
            {vcLabels.selectMakeFirstMessage}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        nested={isNative}
        className="max-w-2xl max-h-[80vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>
            {vcLabels.selectEngineForMake.replace("{make}", selectedMake)}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {isLoadingMakes ? (
            <div className="text-sm text-muted-foreground text-center py-8">
              {vcLabels.loadingEngines}
            </div>
          ) : displayedEngines.length === 0 && !showEngineOtherInput ? (
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground text-center py-4">
                {vcLabels.noEnginesAvailable.replace("{make}", selectedMake)}
              </div>
              {/* Always show Övrigt/Other option if no engines */}
              <Button
                variant="formOption"
                onClick={handleEngineOtherClick}
                className="bg-blue-200 dark:bg-blue-900/30 border-blue-400 w-full"
              >
                {valLabels.other}
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {displayedEngines.map((engineItem) => {
                  const engineName =
                    typeof engineItem === "string"
                      ? engineItem
                      : engineItem.name;
                  const engineId =
                    typeof engineItem === "string" ? engineItem : engineItem.id;
                  return (
                    <Button
                      key={engineId}
                      variant="formOption"
                      onClick={() => handleEngineSelect(engineName)}
                      className={`${
                        selectedEngine === engineName
                          ? "border-accent border-2"
                          : "border-gray-400"
                      } truncate`}
                      title={engineName}
                    >
                      {engineName}
                    </Button>
                  );
                })}
                {/* Övrigt/Other option */}
                {!showEngineOtherInput && (
                  <Button
                    variant="formOption"
                    onClick={handleEngineOtherClick}
                    className={
                      selectedEngine &&
                      !availableEngines.some((e) => {
                        const engineName = typeof e === "string" ? e : e.name;
                        return engineName === selectedEngine;
                      })
                        ? "border-accent border-2 bg-accent/20"
                        : "border-gray-400"
                    }
                  >
                    {valLabels.other}
                  </Button>
                )}
              </div>

              {showEngineOtherInput && (
                <div className="flex gap-2">
                  <Input
                    value={engineOtherText}
                    onChange={(e) => setEngineOtherText(e.target.value)}
                    placeholder={vcLabels.enterEngineOther}
                    maxLength={30}
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleEngineOtherSubmit();
                      }
                    }}
                  />
                  <Button
                    onClick={handleEngineOtherSubmit}
                    disabled={!engineOtherText.trim()}
                  >
                    {btnLabels.save}
                  </Button>
                </div>
              )}

              {availableEngines.length > 7 && (
                <Button
                  variant="ghost"
                  className="w-full flex justify-center gap-2"
                  onClick={() => setShowAllEngines(!showAllEngines)}
                >
                  {showAllEngines
                    ? vcLabels.showLessEngines
                    : vcLabels.showMoreEngines}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform ${
                      showAllEngines ? "rotate-180" : ""
                    }`}
                  />
                </Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
